/**
 * Sacred Frequency Tone Generator
 * 
 * Generates real audio tones using the sacred frequencies from our research.
 * Uses Solfeggio frequencies, A=432Hz tuning, golden ratio harmonics, and chakra tones.
 * 
 * NOT just placeholders - actual working audio generation!
 */

import { audioManager } from '../core/audio/AudioManagerAdapter';
import { SOLFEGGIO_FREQUENCIES, CHAKRA_SYSTEM, A432_TUNING, SCHUMANN_RESONANCES } from './frequencies';

export interface ToneConfig {
  frequency: number;
  duration?: number; // seconds
  volume?: number; // 0-1
  waveform?: OscillatorType;
  fadeIn?: number; // seconds
  fadeOut?: number; // seconds
}

export interface ChordConfig {
  frequencies: number[];
  duration?: number;
  volume?: number;
  waveform?: OscillatorType;
  stagger?: number; // delay between each note in seconds
}

/**
 * Sacred Frequency Generator Class
 */
export class SacredFrequencyGenerator {
  private audioContext: AudioContext | null = null;
  private masterGain: GainNode | null = null;
  private activeOscillators: OscillatorNode[] = [];

  constructor() {
    // Audio context will be retrieved from AudioManager when needed
  }

  /**
   * Initialize with AudioManager's context
   */
  private ensureAudioContext(): boolean {
    if (!audioManager.isReady()) {
      console.warn('AudioManager not initialized yet');
      return false;
    }

    this.audioContext = audioManager.getMainAudioContext();
    this.masterGain = audioManager.getMainMasterGain();
    return true;
  }

  /**
   * Play a single tone
   */
  public async playTone(config: ToneConfig): Promise<void> {
    if (!this.ensureAudioContext() || !this.audioContext || !this.masterGain) {
      throw new Error('Audio not ready');
    }

    const {
      frequency,
      duration = 2,
      volume = 0.3,
      waveform = 'sine',
      fadeIn = 0.1,
      fadeOut = 0.2
    } = config;

    const now = this.audioContext.currentTime;

    // Create oscillator
    const oscillator = this.audioContext.createOscillator();
    oscillator.type = waveform;
    oscillator.frequency.value = frequency;

    // Create gain node for this tone
    const gainNode = this.audioContext.createGain();
    gainNode.gain.value = 0;

    // Connect: oscillator -> gain -> master
    oscillator.connect(gainNode);
    gainNode.connect(this.masterGain);

    // Fade in
    gainNode.gain.setValueAtTime(0, now);
    gainNode.gain.linearRampToValueAtTime(volume, now + fadeIn);

    // Hold
    gainNode.gain.setValueAtTime(volume, now + duration - fadeOut);

    // Fade out
    gainNode.gain.linearRampToValueAtTime(0, now + duration);

    // Start and stop
    oscillator.start(now);
    oscillator.stop(now + duration);

    this.activeOscillators.push(oscillator);

    // Clean up
    oscillator.onended = () => {
      oscillator.disconnect();
      gainNode.disconnect();
      this.activeOscillators = this.activeOscillators.filter(osc => osc !== oscillator);
    };
  }

  /**
   * Play a chord (multiple frequencies simultaneously)
   */
  public async playChord(config: ChordConfig): Promise<void> {
    const {
      frequencies,
      duration = 3,
      volume = 0.3,
      waveform = 'sine',
      stagger = 0
    } = config;

    const volumePerNote = volume / frequencies.length; // Balance volume across notes

    for (let i = 0; i < frequencies.length; i++) {
      const delay = i * stagger;
      
      setTimeout(() => {
        this.playTone({
          frequency: frequencies[i],
          duration,
          volume: volumePerNote,
          waveform,
          fadeIn: 0.1,
          fadeOut: 0.3
        });
      }, delay * 1000);
    }
  }

  /**
   * Play Solfeggio Frequency by name
   */
  public async playSolfeggioTone(name: keyof typeof SOLFEGGIO_FREQUENCIES, duration: number = 3): Promise<void> {
    const tone = SOLFEGGIO_FREQUENCIES[name];
    await this.playTone({
      frequency: tone.frequency,
      duration,
      volume: 0.25,
      waveform: 'sine'
    });
  }

  /**
   * Play complete Solfeggio Scale (ascending)
   */
  public async playSolfeggioScale(duration: number = 8): Promise<void> {
    const frequencies = [
      SOLFEGGIO_FREQUENCIES.UT_396.frequency,
      SOLFEGGIO_FREQUENCIES.RE_417.frequency,
      SOLFEGGIO_FREQUENCIES.MI_528.frequency,
      SOLFEGGIO_FREQUENCIES.FA_639.frequency,
      SOLFEGGIO_FREQUENCIES.SOL_741.frequency,
      SOLFEGGIO_FREQUENCIES.LA_852.frequency,
      SOLFEGGIO_FREQUENCIES.SI_963.frequency
    ];

    const noteDuration = duration / frequencies.length;

    for (let i = 0; i < frequencies.length; i++) {
      const delay = i * noteDuration;
      setTimeout(() => {
        this.playTone({
          frequency: frequencies[i],
          duration: noteDuration * 1.2, // Slight overlap
          volume: 0.3,
          waveform: 'sine'
        });
      }, delay * 1000);
    }
  }

  /**
   * Play Chakra Frequency
   */
  public async playChakraTone(chakraName: keyof typeof CHAKRA_SYSTEM, duration: number = 3): Promise<void> {
    const chakra = CHAKRA_SYSTEM[chakraName];
    await this.playTone({
      frequency: chakra.frequency,
      duration,
      volume: 0.25,
      waveform: 'sine'
    });
  }

  /**
   * Play complete Chakra Scale (root to crown)
   */
  public async playChakraScale(duration: number = 9): Promise<void> {
    const frequencies = [
      CHAKRA_SYSTEM.muladhara.frequency,     // 396 Hz
      CHAKRA_SYSTEM.svadhisthana.frequency,  // 417 Hz
      CHAKRA_SYSTEM.manipura.frequency,      // 528 Hz
      CHAKRA_SYSTEM.anahata.frequency,       // 639 Hz
      CHAKRA_SYSTEM.vishuddha.frequency,     // 741 Hz
      CHAKRA_SYSTEM.ajna.frequency,          // 852 Hz
      CHAKRA_SYSTEM.sahasrara.frequency      // 963 Hz
    ];

    const noteDuration = duration / frequencies.length;

    for (let i = 0; i < frequencies.length; i++) {
      const delay = i * noteDuration;
      setTimeout(() => {
        this.playTone({
          frequency: frequencies[i],
          duration: noteDuration * 1.3,
          volume: 0.3,
          waveform: 'sine'
        });
      }, delay * 1000);
    }
  }

  /**
   * Play A=432Hz Natural Tuning Scale (A major)
   */
  public async play432Scale(duration: number = 8): Promise<void> {
    // A major scale in 432Hz tuning
    // A(432), B(486), C#(546), D(576), E(648), F#(729), G#(819), A(864)
    const frequencies = [432, 486, 546, 576, 648, 729, 819, 864];

    const noteDuration = duration / frequencies.length;

    for (let i = 0; i < frequencies.length; i++) {
      const delay = i * noteDuration;
      setTimeout(() => {
        this.playTone({
          frequency: frequencies[i],
          duration: noteDuration * 1.2,
          volume: 0.3,
          waveform: 'sine'
        });
      }, delay * 1000);
    }
  }

  /**
   * Play Golden Ratio Harmonic Series (based on phi)
   */
  public async playGoldenRatioScale(duration: number = 8): Promise<void> {
    const phi = 1.618033988749895;
    const baseFreq = 256; // C4 in 432Hz tuning

    const frequencies = [
      baseFreq,
      baseFreq * phi,
      baseFreq * phi * phi,
      baseFreq * phi * phi * phi / 4, // Bring back into audible range
      baseFreq * phi * phi * phi * phi / 8
    ];

    const noteDuration = duration / frequencies.length;

    for (let i = 0; i < frequencies.length; i++) {
      const delay = i * noteDuration;
      setTimeout(() => {
        this.playTone({
          frequency: frequencies[i],
          duration: noteDuration * 1.5,
          volume: 0.3,
          waveform: 'sine'
        });
      }, delay * 1000);
    }
  }

  /**
   * Play Fr00t Harmonic Chord (our signature sound)
   * F(432Hz) + R(528Hz) + T(741Hz) = Fr00t essence
   */
  public async playFr00tChord(duration: number = 4): Promise<void> {
    await this.playChord({
      frequencies: [
        432, // F - A432 natural tuning
        528, // R - Solfeggio MI (Love frequency)
        741  // T - Solfeggio SOL (Expression)
      ],
      duration,
      volume: 0.35,
      waveform: 'sine',
      stagger: 0.15
    });
  }

  /**
   * Play Harmony Chord (balanced thirds)
   */
  public async playHarmonyChord(duration: number = 4): Promise<void> {
    await this.playChord({
      frequencies: [256, 320, 384], // Perfect major triad in 432Hz tuning
      duration,
      volume: 0.35,
      waveform: 'sine',
      stagger: 0.1
    });
  }

  /**
   * Play Love Chord (heart-centered frequencies)
   */
  public async playLoveChord(duration: number = 4): Promise<void> {
    await this.playChord({
      frequencies: [
        528, // MI - Solfeggio love frequency
        639, // FA - Connection
        963  // SI - Divine consciousness
      ],
      duration,
      volume: 0.35,
      waveform: 'sine',
      stagger: 0.2
    });
  }

  /**
   * Play Power Chord (authority and strength)
   */
  public async playPowerChord(duration: number = 4): Promise<void> {
    await this.playChord({
      frequencies: [
        396, // UT - Liberation
        528, // MI - Transformation
        852  // LA - Spiritual order
      ],
      duration,
      volume: 0.35,
      waveform: 'sine',
      stagger: 0.1
    });
  }

  /**
   * Play Wisdom Chord (higher consciousness)
   */
  public async playWisdomChord(duration: number = 4): Promise<void> {
    await this.playChord({
      frequencies: [
        639, // FA - Love
        741, // SOL - Expression
        963  // SI - Divine
      ],
      duration,
      volume: 0.35,
      waveform: 'sine',
      stagger: 0.15
    });
  }

  /**
   * Play Schumann Resonance (Earth's heartbeat)
   */
  public async playSchumannResonance(duration: number = 10): Promise<void> {
    await this.playTone({
      frequency: SCHUMANN_RESONANCES.fundamental.frequency, // 7.83 Hz
      duration,
      volume: 0.4,
      waveform: 'sine',
      fadeIn: 2,
      fadeOut: 2
    });
  }

  /**
   * Stop all active tones
   */
  public stopAll(): void {
    if (!this.audioContext) return;

    const now = this.audioContext.currentTime;

    this.activeOscillators.forEach(osc => {
      try {
        osc.stop(now);
      } catch (e) {
        // Already stopped
      }
    });

    this.activeOscillators = [];
  }

  /**
   * Play scale by name
   */
  public async playScaleByName(scaleName: string, duration: number = 8): Promise<void> {
    switch (scaleName) {
      case 'fr00t':
        return this.playFr00tChord(duration / 2);
      case 'solfeggio':
        return this.playSolfeggioScale(duration);
      case 'chakras':
        return this.playChakraScale(duration);
      case 'golden':
        return this.playGoldenRatioScale(duration);
      default:
        return this.playSolfeggioScale(duration);
    }
  }

  /**
   * Play chord by name
   */
  public async playChordByName(chordName: string, duration: number = 4): Promise<void> {
    switch (chordName) {
      case 'fr00t':
        return this.playFr00tChord(duration);
      case 'harmony':
        return this.playHarmonyChord(duration);
      case 'love':
        return this.playLoveChord(duration);
      case 'power':
        return this.playPowerChord(duration);
      case 'wisdom':
        return this.playWisdomChord(duration);
      default:
        return this.playHarmonyChord(duration);
    }
  }
}

// Export singleton instance
export const sacredFrequencyGenerator = new SacredFrequencyGenerator();
export default sacredFrequencyGenerator;

